import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Volume2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAudioManagerOptional } from "@/contexts/AudioManagerContext";

interface AudioPlayerProps {
  track: {
    id: number;
    title: string;
    duration?: number;
    audioUrl?: string;
    category?: string;
  };
  className?: string;
  onPlay?: (trackId: number, currentTime: number) => void;
  onPause?: (trackId: number, currentTime: number) => void;
  onEnd?: (trackId: number, totalDuration: number) => void;
}

export function AudioPlayer({ track, className, onPlay, onPause, onEnd }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(track.duration || 0);
  const [volume, setVolume] = useState(75);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioManager = useAudioManagerOptional();

  // Check if we have a valid audio URL
  const hasAudioUrl = track.audioUrl && track.audioUrl.trim() !== '';

  // Initialize audio element only when user clicks play
  const initializeAudio = async () => {
    if (!hasAudioUrl || audioRef.current) return;

    setIsLoading(true);
    setHasError(false);
    setErrorMessage("");

    try {
      const audio = new Audio();
      audioRef.current = audio;

      // Set initial volume
      audio.volume = volume / 100;

      // Audio event listeners
      const handleCanPlay = () => {
        setIsLoading(false);
        setAudioReady(true);
        if (audio.duration && !isNaN(audio.duration)) {
          setDuration(audio.duration);
        }
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        onEnd?.(track.id, audio.duration || duration);
      };

      const handleError = (e: Event) => {
        console.error("Audio loading error:", e);
        setIsLoading(false);
        setHasError(true);
        setAudioReady(false);

        // More specific error messages based on the error type
        const target = e.target as HTMLAudioElement;
        if (target && target.error) {
          switch (target.error.code) {
            case 1: // MEDIA_ERR_ABORTED
              setErrorMessage("Audio playback was aborted");
              break;
            case 2: // MEDIA_ERR_NETWORK
              setErrorMessage("Network error - please check your connection");
              break;
            case 3: // MEDIA_ERR_DECODE
              setErrorMessage("Audio file is corrupted or in an unsupported format");
              break;
            case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
              setErrorMessage("Audio format not supported by your browser");
              break;
            default:
              setErrorMessage("Audio file not found or unavailable");
          }
        } else {
          setErrorMessage("Audio file not found or unavailable");
        }

        // Log additional info for debugging
        console.error("Audio error details:", {
          audioUrl: track.audioUrl,
          errorCode: target?.error?.code,
          errorMessage: target?.error?.message,
          networkState: target?.networkState,
          readyState: target?.readyState
        });
      };

      const handleLoadedMetadata = () => {
        if (audio.duration && !isNaN(audio.duration)) {
          setDuration(audio.duration);
        }
      };

      const handleLoadStart = () => {
        setIsLoading(true);
        setHasError(false);
        setErrorMessage("");
      };

      const handleCanPlayThrough = () => {
          setIsLoading(false);
      }

      const handlePause = () => {
        setIsPlaying(false);
      };

      const handlePlay = () => {
        setIsPlaying(true);
      };

      // Attach event listeners
      audio.addEventListener('loadstart', handleLoadStart);
      audio.addEventListener('canplay', handleCanPlay);
      audio.addEventListener('canplaythrough', handleCanPlayThrough);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      audio.addEventListener('loadedmetadata', handleLoadedMetadata);
      audio.addEventListener('pause', handlePause);
      audio.addEventListener('play', handlePlay);

      // Start loading the audio - construct proper URL
      const audioUrl = track.audioUrl!.startsWith('/audio/') 
        ? `${window.location.origin}${track.audioUrl}` 
        : track.audioUrl!;

      // Set audio properties for better browser compatibility
      audio.preload = 'metadata';
      audio.crossOrigin = 'anonymous';
      audio.src = audioUrl;

      // Force load the audio
      audio.load();

      // Cleanup function
      return () => {
        audio.removeEventListener('loadstart', handleLoadStart);
        audio.removeEventListener('canplay', handleCanPlay);
        audio.removeEventListener('canplaythrough', handleCanPlayThrough);
        audio.removeEventListener('timeupdate', handleTimeUpdate);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        audio.removeEventListener('pause', handlePause);
        audio.removeEventListener('play', handlePlay);

        if (!audio.paused) {
          audio.pause();
        }
      };
    } catch (error) {
      console.error("Audio initialization error:", error);
      setIsLoading(false);
      setHasError(true);
      setErrorMessage("This sound therapy is not available currently");
    }
  };

  // Update volume when slider changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume / 100;
    }
  }, [volume]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        if (!audioRef.current.paused) {
          audioRef.current.pause();
        }
        audioManager?.unregisterAudio(track.id);
        audioRef.current = null;
      }
    };
  }, [audioManager, track.id]);

  const togglePlay = async () => {
    if (!hasAudioUrl) {
      return;
    }

    // If audio is already initialized
    if (audioRef.current) {
      try {
        if (isPlaying) {
          await audioRef.current.pause();
          setIsPlaying(false);
          onPause?.(track.id, audioRef.current.currentTime);
        } else {
          audioManager?.stopAllExcept(track.id);
          await audioRef.current.play();
          setIsPlaying(true);
          onPlay?.(track.id, audioRef.current.currentTime);
        }
      } catch (error) {
        console.error("Audio playback error:", error);
        setHasError(true);
        setIsPlaying(false);

        if (error instanceof Error) {
          if (error.name === 'NotAllowedError') {
            setErrorMessage("Please click play button to start audio");
          } else if (error.name === 'NotSupportedError') {
            setErrorMessage("Audio format not supported by your browser");
          } else {
            setErrorMessage("Unable to play audio - please try again");
          }
        } else {
          setErrorMessage("Unable to play audio - please try again");
        }
      }
      return;
    }

    // Initialize audio on first click
    await initializeAudio();

    // Use a small timeout to allow the audio element to be ready, then play
    setTimeout(async () => {
      if (audioRef.current) {
        try {
          audioManager?.stopAllExcept(track.id);
          audioManager?.registerAudio(track.id, audioRef.current);
          await audioRef.current.play();
          setIsPlaying(true);
          onPlay?.(track.id, 0);
        } catch (error) {
          console.error("Audio playback error:", error);
          setHasError(true);
          setErrorMessage("Unable to play audio - please try again");
        }
      }
    }, 100);
  };

  const handleSeek = (value: number[]) => {
    if (audioRef.current && audioReady) {
      const newTime = value[0];
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    setVolume(value[0]);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Determine button state and styling
  const getButtonState = () => {
    if (!hasAudioUrl) {
      return { disabled: true, variant: "outline" as const, opacity: "opacity-50" };
    }
    if (hasError) {
      return { disabled: true, variant: "outline" as const, opacity: "opacity-50" };
    }
    if (isLoading) {
      return { disabled: true, variant: "outline" as const, opacity: "opacity-75" };
    }
    return { disabled: false, variant: "outline" as const, opacity: "opacity-100" };
  };

  const buttonState = getButtonState();

  return (
    <div className={cn("bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border", className)}>      
      <div className="space-y-3">
        <h3 className="font-medium text-gray-900 dark:text-white">{track.title}</h3>

        {/* Status Messages */}
        {!hasAudioUrl && (
          <div className="flex items-center space-x-2 text-sm text-amber-600 bg-amber-50 p-2 rounded">
            <AlertCircle className="w-4 h-4" />
            <span>No audio file available for this track</span>
          </div>
        )}

        {hasError && errorMessage && (
          <div className="flex items-center space-x-2 text-sm text-red-600 bg-red-50 p-2 rounded">
            <AlertCircle className="w-4 h-4" />
            <span>{errorMessage}</span>
          </div>
        )}

        {isLoading && (
          <div className="text-sm text-blue-600 bg-blue-50 p-2 rounded flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            <span>Loading audio file...</span>
          </div>
        )}

        {/* Audio Controls */}
        <div className="flex items-center space-x-3">
          <Button
            onClick={togglePlay}
            size="sm"
            variant={buttonState.variant}
            className={cn(
              "w-10 h-10 rounded-full p-0",
              buttonState.opacity,
              hasError && "border-red-300",
              !hasAudioUrl && "border-amber-300"
            )}
            disabled={buttonState.disabled}
            title={
              !hasAudioUrl 
                ? "Audio file not available" 
                : hasError 
                  ? errorMessage 
                  : isLoading 
                    ? "Loading audio..." 
                    : audioReady 
                      ? (isPlaying ? "Pause" : "Play")
                      : "Click to load audio"
            }
          >
            {isLoading ? (
              <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-4 h-4" />
            ) : (
              <Play className="w-4 h-4" />
            )}
          </Button>

          <div className="flex-1 space-y-1">
            <Slider
              value={[currentTime]}
              onValueChange={handleSeek}
              max={duration || 100}
              step={1}
              className={cn(
                "w-full",
                (!audioReady || !hasAudioUrl) && "opacity-50"
              )}
              disabled={!audioReady || !hasAudioUrl}
            />
            <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>
        </div>

        {/* Volume Control */}
        <div className="flex items-center space-x-2">
          <Volume2 className={cn(
            "w-4 h-4 text-gray-500",
            !hasAudioUrl && "opacity-50"
          )} />
          <Slider
            value={[volume]}
            onValueChange={handleVolumeChange}
            max={100}
            step={1}
            className={cn(
              "w-20",
              !hasAudioUrl && "opacity-50"
            )}
            disabled={!hasAudioUrl}
          />
          <span className="text-xs text-gray-500 w-8">{volume}%</span>
        </div>
      </div>
    </div>
  );
}